import {
  ArtOrientation,
  ArtSize,
  Gender,
  ProductQuality,
  Shape,
} from "@/Apollo/schema";
import { objectKeys } from "@/common/utils/object.utils";

/**
 * Translation keys for the product attributes the gateway hands back as enums — the create-ad
 * wizard picks them, the account's ad details renders them.
 *
 * These used to live with the gateway-backed search filters (WHO-5636 deleted that), which is the
 * only reason a wizard ever imported from a search feature. Search itself no longer needs them: the
 * service labels its own facets.
 */

export const ART_SIZE_MAP: Record<ArtSize, string> = {
  [ArtSize.Mini]: "listing:filter-art-size-mini",
  [ArtSize.Small]: "listing:filter-art-size-small",
  [ArtSize.Medium]: "listing:filter-art-size-medium",
  [ArtSize.Large]: "listing:filter-art-size-large",
  [ArtSize.XLarge]: "listing:filter-art-size-x-large",
};

export const ART_ORIENTATION_MAP: Record<ArtOrientation, string> = {
  [ArtOrientation.Landscape]: "listing:filter-art-orientation-landscape",
  [ArtOrientation.Square]: "listing:filter-art-orientation-square",
  [ArtOrientation.Portrait]: "listing:filter-art-orientation-portrait",
};

export const QUALITY_MAP: Record<ProductQuality, string> = {
  [ProductQuality.Good]: "common:condition-good",
  [ProductQuality.Great]: "common:condition-great",
  [ProductQuality.Perfect]: "common:condition-perfect",
};

export const QUALITY_MAP_KEYS = objectKeys(QUALITY_MAP);

export const GENDER_MAP: Record<Gender, string> = {
  [Gender.Men]: "common:common-gender-men",
  [Gender.Women]: "common:common-gender-women",
  [Gender.Unisex]: "common:common-gender-unisex",
};

export const GENDER_MAP_KEYS = objectKeys(GENDER_MAP);

export const SHAPE_MAP: Record<Shape, string> = {
  [Shape.Organic]: "shape:shape-organic",
  [Shape.Other]: "shape:shape-other",
  [Shape.Oval]: "shape:shape-oval",
  [Shape.Rectangle]: "shape:shape-rectangle",
  [Shape.Round]: "shape:shape-round",
  [Shape.Square]: "shape:shape-square",
  [Shape.Triangle]: "shape:shape-triangle",
};

export const SHAPE_MAP_KEYS = objectKeys(SHAPE_MAP);

/** The swatch for `Color.Multi`, which has no single hex value to paint. */
export const MULTI_COLOR_GRADIENT =
  "linear-gradient(" +
  "rgba(255, 0, 0, 1) 0%, " +
  "rgba(255, 154, 0, 1) 10%, " +
  "rgba(208, 222, 33, 1) 20%, " +
  "rgba(79, 220, 74, 1) 30%, " +
  "rgba(63, 218, 216, 1) 40%, " +
  "rgba(47, 201, 226, 1) 50%, " +
  "rgba(28, 127, 238, 1) 60%, " +
  "rgba(95, 21, 242, 1) 70%, " +
  "rgba(186, 12, 248, 1) 80%, " +
  "rgba(251, 7, 217, 1) 90%, " +
  "rgba(255, 0, 0, 1) 100%)";

/** The swatch for `Color.Silver`, for the same reason. */
export const SILVER_GRADIENT =
  "linear-gradient(" +
  "rgba(128, 128, 128, 1) 0%, " +
  "rgba(225, 225, 225, 1) 10%, " +
  "rgba(128, 128, 128, 1) 30%, " +
  "rgba(225, 225, 225, 1) 60%, " +
  "rgba(225, 225, 225, 1) 80%, " +
  "rgba(128, 128, 128, 1) 90%, " +
  "rgba(128, 128, 128, 1) 100%)";
